/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/phone/setup/QaPhonePlanStep.tsx
 * FILE_ID: JOBHUB-QA-PHONE-PLAN-001
 * PURPOSE: Combine vs separate, include quote-derived checks, scope name, Review.
 */
import { useState } from 'react'
import { Layers, Loader2, SplitSquareHorizontal } from 'lucide-react'
import type { QaCreateMode, QaWriteTarget } from '../../../JobQaSetupCard'
import { QaWriteTargetDialog } from '../../QaWriteTargetDialog'

export function QaPhonePlanStep({
  createMode,
  onCreateModeChange,
  writeTarget = 'create',
  onWriteTargetChange,
  showWriteTarget = false,
  selectedCount,
  onCreate,
  includeQuoteDerivedItems,
  onIncludeQuoteDerivedItemsChange,
  scopeName,
  onScopeNameChange,
  createPending,
}: {
  createMode: QaCreateMode
  onCreateModeChange: (mode: QaCreateMode) => void
  writeTarget?: QaWriteTarget
  onWriteTargetChange?: (target: QaWriteTarget) => void
  showWriteTarget?: boolean
  selectedCount: number
  onCreate: () => void
  includeQuoteDerivedItems?: boolean
  onIncludeQuoteDerivedItemsChange?: (value: boolean) => void
  scopeName: string
  onScopeNameChange: (name: string) => void
  createPending: boolean
}) {
  const [askWhere, setAskWhere] = useState(false)
  const requestCreate = (mode: QaCreateMode = createMode) => {
    if (mode === 'combine' || showWriteTarget) {
      setAskWhere(true)
      return
    }
    onCreate()
  }
  return (
    <div className="space-y-3">
      <div role="radiogroup" aria-label="How to create QA from the selected quotes" className="flex flex-col gap-2">
        {([
          ['combine', 'Combine into one QA', Layers],
          ['separate', 'Separate QA per quote', SplitSquareHorizontal],
        ] as const).map(([mode, label, Icon]) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={createMode === mode}
            onClick={() => {
              onCreateModeChange(mode)
              if (selectedCount > 0) requestCreate(mode)
            }}
            className={[
              'flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-sm',
              createMode === mode ? 'border-primary bg-primary/5' : 'border-border',
            ].join(' ')}
          >
            <Icon className="h-4 w-4" aria-hidden />
            {label}
          </button>
        ))}
      </div>
      {showWriteTarget && (
        <div role="radiogroup" aria-label="Where this QA goes" className="flex flex-col gap-2">
          {([
            ['replace', 'Replace selected'],
            ['create', 'Create new'],
          ] as const).map(([target, label]) => (
            <button
              key={target}
              type="button"
              role="radio"
              aria-checked={writeTarget === target}
              onClick={() => onWriteTargetChange?.(target)}
              className={[
                'flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-sm',
                writeTarget === target ? 'border-primary bg-primary/5' : 'border-border',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {onIncludeQuoteDerivedItemsChange && (
        <label className="flex min-h-[44px] items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeQuoteDerivedItems}
            onChange={(event) => onIncludeQuoteDerivedItemsChange(event.target.checked)}
          />
          Include quote-derived checks after the default area items
        </label>
      )}
      <input
        type="text"
        value={scopeName}
        onChange={(event) => onScopeNameChange(event.target.value)}
        placeholder={createMode === 'separate' ? 'Scope name (optional)' : 'QA list name (optional)'}
        className="min-h-[44px] w-full rounded-lg border border-border bg-background px-3 text-base"
        aria-label="QA list or scope name"
      />
      <button
        type="button"
        onClick={() => requestCreate(createMode)}
        disabled={selectedCount === 0 || createPending}
        className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-50"
      >
        {createPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
        Create QA{selectedCount > 1 ? ` from ${selectedCount} quotes` : ''}
      </button>
      {askWhere ? (
        <QaWriteTargetDialog
          onCancel={() => setAskWhere(false)}
          onChoose={(target, name) => {
            onWriteTargetChange?.(target)
            if (name) onScopeNameChange(name)
            setAskWhere(false)
            onCreate()
          }}
        />
      ) : null}
    </div>
  )
}
